import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Search, Wrench, Filter, X, AlertTriangle, CheckCircle, HelpCircle, Link2, Monitor, Power, PowerOff,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import { toast } from 'sonner';

const HypervIntegrationReport = () => {
  const [loading, setLoading] = useState(true);
  const [vms, setVMs] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, pages: 1 });
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [sortConfig, setSortConfig] = useState({ field: 'integration_services_state', dir: 'asc' });
  const [activeStatFilter, setActiveStatFilter] = useState('issues');

  const fetchVMs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir,
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      const res = await api.get(`/reports/virt/hyperv/tools?${params}`);
      setVMs(res.data.items);
      setPagination(prev => ({ ...prev, total: res.data.total, pages: res.data.pages }));
    } catch (err) {
      toast.error('Error al cargar Integration Services');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => { try { setStats((await api.get('/reports/virt/hyperv/tools/stats')).data); } catch (e) { console.error(e); } };

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => {
    const t = setTimeout(fetchVMs, 300);
    return () => clearTimeout(t);
  }, [pagination.page, filters, sortConfig]);

  const handleSort = (field) => setSortConfig(prev => ({ field, dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc' }));
  const SortIcon = ({ field }) => sortConfig.field !== field
    ? <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />
    : (sortConfig.dir === 'asc' ? <ChevronUp className="h-3 w-3 text-primary" /> : <ChevronDown className="h-3 w-3 text-primary" />);
  const SortableHeader = ({ field, children, className = '' }) => (
    <th className={`p-4 cursor-pointer hover:bg-muted/70 select-none ${className}`} onClick={() => handleSort(field)}>
      <div className="flex items-center gap-1">{children}<SortIcon field={field} /></div>
    </th>
  );

  const handleStatFilter = (t) => {
    setActiveStatFilter(t);
    setPagination(prev => ({ ...prev, page: 1 }));
    const map = { ok: 'ok', unknown: 'unknown' };
    setFilters({ search: '', status: map[t] || '' });
  };

  const getISBadge = (state) => {
    if (!state) return <Badge variant="outline">Desconocido</Badge>;
    if (state === 'Ok') return <Badge className="bg-green-500 text-white">OK</Badge>;
    return <Badge className="bg-yellow-500 text-black">{state}</Badge>;
  };
  const getISIcon = (state) => {
    if (!state) return <HelpCircle className="h-4 w-4 text-muted-foreground" />;
    if (state === 'Ok') return <CheckCircle className="h-4 w-4 text-green-500" />;
    return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
  };

  const clearFilters = () => { setFilters({ search: '', status: '' }); setActiveStatFilter('issues'); };
  const hasFilters = filters.search || filters.status;

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Reporte Integration Services (Hyper-V)</h1>
          <p className="text-sm text-muted-foreground">VMs cuyo estado de Integration Services no es "Ok"</p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className={`card-subtle cursor-pointer transition-all hover:border-yellow-500/50 ${activeStatFilter === 'issues' ? 'border-yellow-500 ring-1 ring-yellow-500/20' : ''}`} onClick={() => handleStatFilter('issues')} data-testid="stat-issues"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-yellow-500">{stats.with_issues || 0}</p><p className="text-xs text-muted-foreground">Con Problemas</p></CardContent></Card>
            <Card className={`card-subtle cursor-pointer transition-all hover:border-green-500/50 ${activeStatFilter === 'ok' ? 'border-green-500 ring-1 ring-green-500/20' : ''}`} onClick={() => handleStatFilter('ok')} data-testid="stat-ok"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-green-500">{stats.ok || 0}</p><p className="text-xs text-muted-foreground">OK</p></CardContent></Card>
            <Card className={`card-subtle cursor-pointer transition-all hover:border-muted-foreground/50 ${activeStatFilter === 'unknown' ? 'border-muted-foreground ring-1 ring-muted-foreground/20' : ''}`} onClick={() => handleStatFilter('unknown')} data-testid="stat-unknown"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-muted-foreground">{stats.unknown || 0}</p><p className="text-xs text-muted-foreground">Desconocido</p></CardContent></Card>
            <Card className="card-subtle"><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{stats.total || 0}</p><p className="text-xs text-muted-foreground">Total VMs</p></CardContent></Card>
          </div>
        )}

        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2"><Filter className="h-4 w-4 text-muted-foreground" /><span className="text-sm font-medium">Filtros:</span></div>
              <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-md">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input placeholder="Buscar VM o servidor..." value={filters.search}
                  onChange={(e) => { setFilters(prev => ({ ...prev, search: e.target.value })); setActiveStatFilter(''); }}
                  className="h-9" data-testid="integration-search-input" />
              </div>
              <Select value={filters.status || 'issues'} onValueChange={(v) => { setFilters(prev => ({ ...prev, status: v === 'issues' ? '' : v })); setActiveStatFilter(v); }}>
                <SelectTrigger className="w-44 h-9" data-testid="integration-status-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="issues">Con Problemas</SelectItem>
                  <SelectItem value="ok">OK</SelectItem>
                  <SelectItem value="unknown">Desconocido</SelectItem>
                </SelectContent>
              </Select>
              {hasFilters && filters.status !== '' && <Button variant="ghost" size="sm" onClick={clearFilters}><X className="h-4 w-4 mr-1" /> Limpiar</Button>}
            </div>
          </CardContent>
        </Card>

        <Card className="card-subtle">
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="integration-table">
              <thead className="bg-muted/50">
                <tr className="border-b">
                  <SortableHeader field="name" className="text-left">VM</SortableHeader>
                  <SortableHeader field="integration_services_state" className="text-center justify-center">INT. SERVICES</SortableHeader>
                  <SortableHeader field="integration_services_version" className="text-left">VERSIÓN</SortableHeader>
                  <SortableHeader field="state" className="text-center justify-center">ESTADO VM</SortableHeader>
                  <SortableHeader field="guest_os" className="text-left">SISTEMA OPERATIVO</SortableHeader>
                  <SortableHeader field="server_hostname" className="text-left">SERVIDOR</SortableHeader>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center p-8 text-muted-foreground">Cargando...</td></tr>
                ) : vms.length === 0 ? (
                  <tr><td colSpan={6} className="text-center p-8 text-muted-foreground">No hay VMs con este estado</td></tr>
                ) : vms.map(vm => (
                  <tr key={vm.id} className="border-b hover:bg-muted/30" data-testid={`integration-row-${vm.id}`}>
                    <td className="p-4"><div className="flex items-center gap-2"><Monitor className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{vm.name}</span></div></td>
                    <td className="p-4 text-center"><div className="flex items-center justify-center gap-2">{getISIcon(vm.integration_services_state)}{getISBadge(vm.integration_services_state)}</div></td>
                    <td className="p-4 text-sm text-muted-foreground">{vm.integration_services_version && vm.integration_services_version !== '0.0' ? vm.integration_services_version : '-'}</td>
                    <td className="p-4 text-center">
                      {vm.state === 'running' ? <div className="flex items-center justify-center gap-1"><Power className="h-4 w-4 text-green-500" /><span className="text-xs">On</span></div> : <div className="flex items-center justify-center gap-1"><PowerOff className="h-4 w-4 text-red-500" /><span className="text-xs">{vm.state}</span></div>}
                    </td>
                    <td className="p-4 text-sm">{vm.guest_os || '-'}</td>
                    <td className="p-4">
                      {vm.server_id ? (
                        <Link to={`/assets/servers?search=${encodeURIComponent(vm.server_hostname)}`} className="flex items-center gap-1 text-blue-500 hover:underline text-sm"><Link2 className="h-3 w-3" />{vm.server_hostname}</Link>
                      ) : <span className="text-muted-foreground">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {pagination.pages > 1 && (
          <div className="flex justify-center items-center gap-4">
            <Button variant="outline" size="sm" disabled={pagination.page === 1} onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm text-muted-foreground">Página {pagination.page} de {pagination.pages} ({pagination.total} VMs)</span>
            <Button variant="outline" size="sm" disabled={pagination.page === pagination.pages} onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default HypervIntegrationReport;
